// Unified Bio Page Renderer — slot-driven.
//
// Aug 2026: every visual decision comes from `page.style`, the typed slot
// spec defined in backend/bio/spec.py and served from bio/presets.py. This
// file knows NO preset by name and branches on none; adding a theme is a
// data change. backend/tests/test_bio_presets.py enforces both — it greps
// this file for every shipped preset id, and renders a synthetic sixth
// preset that uses slot values none of the five do.
//
// What this replaced: a THEME_PRESETS table living here that supplied
// bg/text/muted per theme NAME, which `page.background_color` then partly
// overrode, with text colour chosen from the name rather than the paint.
// Two live pages rendered at 1.20:1 and 2.99:1 as a direct result.
//
// Props — `isPreview` is a back-compat alias that expands to the old
// combination; the six real flags are independent so theme thumbnails can
// be compact and inert while still showing chrome and loading fonts.
import React, { useEffect, useState } from "react";
import {
  Instagram, Youtube, Facebook, Twitter, Mail, User, ExternalLink, CheckCircle,
  Play,
} from "lucide-react";
import { platformFromUrl } from "../../lib/bio-platforms";
import {
  animCss, avatarCss, borderCss, buttonCss, captionCss, fontStack, fontsUsed,
  radiusCss, shadowCss, socialCss, textCss, tileColumns, tileCss, FONT_MAP,
  shopCardCss, shopImageCss, shopTextCss, shopBuyCss, shopMoreCss,
  SHOP_3UP_MIN_WIDTH, SHOP_VISIBLE_FALLBACK,
} from "../../lib/bioStyle";
import PublicPageActionHeader from "./PublicPageActionHeader";
// The layout->sections rule, imported rather than restated. This file used
// to carry its own copy of the three predicates; BioEditorPage imported
// them from bioPageState and this file re-derived them inline, so widening
// `shop` had to be done in two places or the editor would offer a section
// the renderer refused to draw. Same shape as the four editor/live
// divergences previewLiveParity.test.js exists for.
import { showsGrid, showsLinks, showsShop, sortByOrder } from "../../lib/bioPageState";

export { FONT_MAP };

/** Keyframes the animation slot references, plus the shop grid rule.
 *
 * THE SHOP GRID USES A CONTAINER QUERY, NOT A MEDIA QUERY, and that is
 * load-bearing rather than fashionable. The theme preview renders the
 * page inside a narrow phone frame on a wide desktop viewport: a media
 * query would read the DESKTOP width there and show a creator 3-up while
 * their actual visitors on a phone get 2-up. The container query reads
 * the width the page is really being painted into, so the preview and
 * the live page follow one rule and a creator sees what ships.
 *
 * Falling back to 2 columns where `@container` is unsupported is the
 * safe direction: 2-up fits everywhere, 3-up is what clips.
 */
const KEYFRAMES = `
@keyframes tdFade { from { opacity: 0 } to { opacity: 1 } }
@keyframes tdSlide { from { opacity: 0; transform: translateY(14px) } to { opacity: 1; transform: none } }
@media (prefers-reduced-motion: reduce) {
  [data-td-anim] { animation: none !important; }
}
.td-shop-wrap { container-type: inline-size; }
.td-shop-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); }
@container (min-width: ${SHOP_3UP_MIN_WIDTH}px) {
  .td-shop-grid { grid-template-columns: repeat(3, minmax(0, 1fr)); }
}`;

function useKeyframes(enabled) {
  useEffect(() => {
    if (!enabled || typeof document === "undefined") return;
    if (document.getElementById("td-bio-keyframes")) return;
    const el = document.createElement("style");
    el.id = "td-bio-keyframes";
    el.textContent = KEYFRAMES;
    document.head.appendChild(el);
  }, [enabled]);
}

/** Google Fonts for every family this preset uses (up to 3 roles). */
function useFonts(style, enabled) {
  const families = fontsUsed(style).join("|");
  useEffect(() => {
    if (!enabled || !families || typeof document === "undefined") return;
    families.split("|").forEach((family) => {
      const id = `gfont-${family.replace(/\s/g, "")}`;
      if (document.getElementById(id)) return;
      const link = document.createElement("link");
      link.id = id;
      link.rel = "stylesheet";
      link.href =
        `https://fonts.googleapis.com/css2?family=${encodeURIComponent(family)}` +
        `:wght@300;400;500;600;700;800&display=swap`;
      document.head.appendChild(link);
    });
  }, [families, enabled]);
}

/**
 * Shown instead of a blank screen when a page has no resolvable style.
 *
 * Two audiences, one component. The visitor gets a plain, un-alarming
 * message rather than a white void; the operator gets an alert, because a
 * page rendering nothing is indistinguishable from a CDN fault from the
 * outside and nobody thinks to open the document first.
 *
 * `silent` suppresses the report for thumbnails and previews — those are
 * rendered from synthetic data and a malformed one is a developer's
 * problem, not a production incident.
 */
export function BioRenderError({ slug, silent = false }) {
  const reported = React.useRef(false);
  useEffect(() => {
    if (silent || reported.current || !slug) return;
    reported.current = true;
    const base =
      (typeof process !== "undefined" && process.env && process.env.REACT_APP_BACKEND_URL) || "";
    // Best-effort. If reporting fails the visitor still sees the message —
    // an error page that errors is worse than one that goes unlogged.
    fetch(`${base}/api/bio/render-error`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ slug, reason: "missing_style" }),
      credentials: "omit",
      keepalive: true,
    }).catch(() => {});
  }, [slug, silent]);

  return (
    <div
      data-testid="bio-render-error"
      style={{
        minHeight: "100vh", display: "flex", alignItems: "center",
        justifyContent: "center", padding: "2rem", background: "#FBFBFC",
        color: "#14161C", fontFamily: "system-ui, sans-serif", textAlign: "center",
      }}
    >
      <div style={{ maxWidth: "26rem" }}>
        <h1 style={{ fontSize: "1.25rem", margin: "0 0 0.5rem", fontWeight: 600 }}>
          This page isn&apos;t set up yet
        </h1>
        <p style={{ margin: 0, color: "#5A6070", lineHeight: 1.6 }}>
          It has no theme, so there&apos;s nothing to show. If this is your page,
          open the editor and pick a theme — it&apos;ll be live again straight away.
        </p>
        <p style={{ margin: "1rem 0 0", color: "#8A8F9A", fontSize: "0.8125rem" }}>
          We&apos;ve been notified.
        </p>
      </div>
    </div>
  );
}

const SOCIAL_ICONS = {
  instagram: Instagram, youtube: Youtube, twitter: Twitter,
  facebook: Facebook, email: Mail,
  whatsapp: (props) => (
    <svg viewBox="0 0 24 24" fill="currentColor" {...props} aria-hidden="true">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
    </svg>
  ),
};
const SOCIAL_ORDER = ["instagram", "youtube", "twitter", "facebook", "whatsapp", "email"];

/**
 * The product grid, plus the in-place reveal.
 *
 * `shop.visible` products show; "see more" reveals the REST IN PLACE
 * rather than navigating anywhere. That is why this needs state and a
 * component of its own, and why the button is not a link.
 *
 * Columns are decided by `.td-shop-grid` (a container query, see
 * KEYFRAMES) rather than by a prop, so the preview and the live page
 * cannot disagree about how many fit.
 */
/**
 * Covers for the Instagram embeds.
 *
 * ═══════════════════════════════════════════════════════════════════
 *  WHY THE THUMBNAIL IS FETCHED AND NOT STORED
 * ═══════════════════════════════════════════════════════════════════
 * Instagram's og:image is a SIGNED CDN URL with an expiry. Resolving it
 * once at save time and persisting it would give every page a set of
 * covers that work on the day they were added and 404 a few weeks later
 * — the same trap the automation code documents about `thumbnail_url`.
 *
 * So it is resolved at render time through `GET /api/bio/ig-metadata`,
 * which is public and caches per canonical URL for 24h ACROSS all
 * tenants. A page with six embeds costs six cache hits.
 *
 * `enabled` is the renderer's existing `fetchRemoteMedia` prop, which
 * until now was declared, passed `false` by all four callers, and read
 * by nothing. It gets its intended job here: previews and theme
 * thumbnails render the custom cover or the branded fallback and make no
 * requests; the public page resolves for real.
 */
function useEmbedCovers(embeds, enabled) {
  const [covers, setCovers] = useState({});
  //: Stable across renders so the effect does not re-fire on every
  //: keystroke in the editor, where `embeds` is a fresh array each time.
  const urls = embeds.map((e) => e.embed_url).filter(Boolean).join("|");

  useEffect(() => {
    if (!enabled || !urls) return undefined;
    let alive = true;
    const controller = typeof AbortController !== "undefined"
      ? new AbortController() : null;

    (async () => {
      for (const url of urls.split("|")) {
        try {
          const r = await fetch(
            `/api/bio/ig-metadata?url=${encodeURIComponent(url)}`,
            controller ? { signal: controller.signal } : undefined,
          );
          const d = await r.json();
          if (!alive) return;
          // Only a real thumbnail is recorded. The endpoint answers 200
          // with `thumbnail_url: null` when the scrape fails, and writing
          // that in would be indistinguishable from "not fetched yet".
          if (d?.thumbnail_url) {
            setCovers((prev) => ({ ...prev, [url]: d.thumbnail_url }));
          }
        } catch {
          /* A dead cover falls back to the branded tile. Never fatal:
             this runs on a page whose whole job is being viewable. */
        }
      }
    })();

    return () => { alive = false; controller?.abort(); };
  }, [urls, enabled]);

  return covers;
}

/**
 * The Instagram posts & reels section.
 *
 * ═══════════════════════════════════════════════════════════════════
 *  IT DID NOT EXIST UNTIL 2026-08-22
 * ═══════════════════════════════════════════════════════════════════
 * `embed_blocks` had an editor (BioEditorPage's "Instagram Posts &
 * Reels"), a validator, a place in RENDERER_PAGE_KEYS, a backend field
 * on both the private and the public route, and a cover-fetching
 * endpoint. The one thing missing was the render — so a creator added a
 * reel, saw it accepted, saved it, and it appeared on neither the
 * preview nor the live page (P, 2026-08-22).
 *
 * previewLiveParity.test.js derives the page keys FROM this file, so
 * reading `page.embed_blocks` here is also what puts embeds under that
 * guard for the first time.
 *
 * Styling reuses the tile slots the image grid uses, so the section
 * inherits whatever preset the page is on rather than introducing a
 * sixth look.
 */
function EmbedGrid({
  embeds, covers, style, columns, spacing, canvas, isCompact, isInteractive,
  anim, animOffset, onEmbedClick,
}) {
  return (
    <div
      data-testid={`${isCompact ? "preview-" : ""}embed-grid`}
      style={{
        display: "grid",
        gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
        gap: `${spacing.tile}rem`,
      }}
    >
      {embeds.map((emb, i) => {
        const cover = emb.custom_cover_url || covers[emb.embed_url] || null;
        const isReel = /\/(reel|tv)\//i.test(emb.embed_url || "");
        return (
          <button
            key={emb.id || i}
            type="button"
            data-td-anim
            data-testid={`${isCompact ? "preview-" : ""}embed-item-${i}`}
            onClick={isInteractive ? () => onEmbedClick(emb) : undefined}
            aria-label={isReel ? "Open reel on Instagram" : "Open post on Instagram"}
            style={{
              position: "relative",
              padding: 0,
              border: "none",
              background: "none",
              cursor: isInteractive ? "pointer" : "default",
              ...anim(animOffset + i),
            }}
          >
            {cover ? (
              <img
                src={cover}
                alt=""
                loading="lazy"
                style={tileCss(style)}
                // A signed URL that has expired must not leave the
                // browser's broken glyph in the grid. Hiding the image
                // reveals the branded tile painted underneath it.
                onError={(e) => { e.currentTarget.style.visibility = "hidden"; }}
              />
            ) : null}

            {/* BRANDED FALLBACK, PAINTED ALWAYS, UNDERNEATH.
                Not an `else`: the cover can fail AFTER it renders, and an
                `onError` that hides the image would then leave an empty
                hole. Under it there is always a tile that reads as an
                Instagram post. */}
            <div
              aria-hidden="true"
              style={{
                ...tileCss(style),
                ...(cover ? { position: "absolute", inset: 0, zIndex: -1 } : {}),
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: canvas.dark
                  ? "rgba(255,255,255,0.07)"
                  : "rgba(0,0,0,0.07)",
              }}
            >
              <Instagram
                style={{ width: "1.5rem", height: "1.5rem", opacity: 0.5, color: canvas.text }}
              />
            </div>

            {/* Reels get the play affordance a static cover cannot carry. */}
            {isReel && (
              <span
                aria-hidden="true"
                style={{
                  position: "absolute",
                  right: "0.4rem",
                  bottom: "0.4rem",
                  width: "1.35rem",
                  height: "1.35rem",
                  borderRadius: "999px",
                  background: "rgba(0,0,0,0.55)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Play style={{ width: "0.7rem", height: "0.7rem", color: "#fff" }} />
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

function ShopGrid({ products, style, isCompact, isInteractive, anim, onProductClick }) {
  const visible = style.shop.visible ?? SHOP_VISIBLE_FALLBACK;
  const [expanded, setExpanded] = React.useState(false);
  const shown = expanded ? products : products.slice(0, visible);
  const hidden = products.length - shown.length;

  return (
    <div className="td-shop-wrap">
      <div
        className="td-shop-grid"
        data-testid={`${isCompact ? "preview-" : ""}shop-grid`}
        style={{ gap: `${style.shop.gap}rem` }}
      >
        {shown.map((product, i) => (
          <div
            key={product.id || i}
            data-td-anim
            data-testid={`${isCompact ? "preview-" : ""}shop-card-${i}`}
            style={{ ...shopCardCss(style), ...anim(i) }}
          >
            {product.image_url ? (
              <img
                src={product.image_url}
                alt={product.title || ""}
                loading="lazy"
                style={shopImageCss(style)}
                onError={(e) => { e.currentTarget.style.visibility = "hidden"; }}
              />
            ) : (
              // Keep the image slot's own treatment (radius, mat, ratio)
              // so an imageless product still reads as the theme.
              <div style={shopImageCss(style)} aria-hidden="true" />
            )}

            <div style={shopTextCss(style, "title")}>{product.title}</div>

            {product.price ? (
              <div style={shopTextCss(style, "price")}>
                {product.currency ? `${product.currency} ` : ""}{product.price}
              </div>
            ) : null}

            {(product.links || []).length > 0 && (
              <button
                type="button"
                data-testid={`${isCompact ? "preview-" : ""}shop-buy-${i}`}
                onClick={isInteractive ? () => onProductClick(product, product.links[0]) : undefined}
                style={{ ...shopBuyCss(style), cursor: isInteractive ? "pointer" : "default" }}
              >
                {product.links[0].label || "Buy now"}
              </button>
            )}
          </div>
        ))}
      </div>

      {hidden > 0 && (
        <button
          type="button"
          data-testid={`${isCompact ? "preview-" : ""}shop-more`}
          onClick={() => setExpanded(true)}
          style={{
            ...shopMoreCss(style),
            marginTop: `${style.shop.gap}rem`,
            cursor: isInteractive ? "pointer" : "default",
          }}
        >
          {`See all ${products.length} products`}
        </button>
      )}
    </div>
  );
}

export default function BioPageRenderer({
  page,
  isPreview = false,
  compact,
  interactive,
  fetchRemoteMedia,
  showChrome,
  loadFonts,
  animate,
  scale = 1,
  leadState = null,
  handlers = {},
}) {
  const isCompact = compact ?? isPreview;
  const isInteractive = interactive ?? !isPreview;
  const wantChrome = showChrome ?? !isPreview;
  const wantFonts = loadFonts ?? !isPreview;
  // Entrance animation is its own flag. The theme picker renders five
  // presets at once and five staggered entrances firing together is
  // chaos, not delight — so the picker turns it off while the
  // content-step preview, where there is exactly one page, keeps it.
  const wantAnim = animate ?? (interactive ?? !isPreview);

  const style = page?.style;
  useFonts(style, wantFonts);
  useKeyframes(!!style);

  // Embeds are NOT layout-gated. `showsGrid` / `showsLinks` / `showsShop`
  // exist because those three collections compete for the same visual
  // slot; posts & reels are their own section and the editor offers them
  // on every layout, so the only gate is having one with a URL.
  //
  // Derived HERE, with the other hooks, because there is an early return
  // below for a page with no style — and a hook after a conditional
  // return is a hook that runs in a different order on the render where
  // the style is missing.
  const embeds = (page?.embed_blocks || []).filter((e) => e?.embed_url);
  const embedCovers = useEmbedCovers(embeds, fetchRemoteMedia ?? isInteractive);

  // No style = an unsaved or malformed document. We deliberately do NOT
  // fall back to a default theme: a guessed theme is how the old default
  // silently replaced a creator's choice.
  //
  // But we do not render nothing either. A page that paints a blank screen
  // is the failure that looks like a CDN problem and gets debugged for an
  // hour before anyone opens the document. So: say what happened, and tell
  // someone (see BioRenderError -> POST /api/bio/render-error, which is
  // throttled to one alert per page per hour).
  if (!style) {
    return <BioRenderError slug={page?.slug} silent={isCompact} />;
  }

  const { canvas, spacing, fonts, animation } = style;
  const emailCfg = page?.email_capture || {};
  const layout = page?.layout_type || "classic";
  const columns = tileColumns(layout);

  const onSocialClick = handlers.onSocialClick || (() => {});
  const onButtonClick = handlers.onButtonClick || (() => {});
  const onGridClick = handlers.onGridClick || (() => {});

  const anim = (i) => animCss(animation, i, wantAnim);

  // Whether a section is ALLOWED here. Whether it actually draws is
  // decided further down by whether it has any content -- every one of
  // these is behind a `.length > 0`, which is what lets a shop page with
  // nothing but products render as products alone.
  const showGrid = showsGrid(layout);
  const showLinks = showsLinks(layout);
  const showShop = showsShop(layout);
  const shopProducts = showShop ? (page?.shop_products || []) : [];
  const gridItems = showGrid ? (page?.grid_items || []) : [];
  // Sorted by `order`, not drawn in array order. See the note in
  // lib/bioPageState: the field existed and nothing read it, so a
  // reordered page would have saved correctly and rendered wrong.
  const links = showLinks
    ? sortByOrder(page?.button_links || []).filter((l) => l.is_active !== false)
    : [];
  const socials = SOCIAL_ORDER.filter((k) => page?.social_links?.[k]);

  const scaled = scale !== 1;
  const px = (rem) => `${rem}rem`;

  const inner = (
    <div
      data-testid={isCompact ? "bio-preview" : "public-bio-page"}
      style={{
        // Fill the frame. Compact renders sit inside a fixed-size
        // preview window; without this the canvas stops at the last
        // element and the container shows through underneath.
        minHeight: isCompact ? "100%" : "100vh",
        background: canvas.background,
        color: canvas.text,
        fontFamily: fontStack(fonts, "body"),
        display: "flex",
        flexDirection: "column",
        ...(scaled
          ? { width: `${100 / scale}%`, transform: `scale(${scale})`, transformOrigin: "top left" }
          : {}),
      }}
    >
      <PublicPageActionHeader page={page} hasDarkBg={canvas.dark} showChrome={wantChrome} />

      <div
        style={{
          padding: `1rem ${px(spacing.hpad)} 1.125rem`,
          display: "flex",
          flexDirection: "column",
          gap: px(spacing.block),
          flex: "1 1 auto",
          // A link-in-bio page is a phone-shaped artefact and this column
          // had no width limit at all, so on a 1920px desktop it painted
          // 1920px wide: the shop's container query saw > 640px, went
          // 3-up, and each product card came out around 600px across
          // (P, 2026-08-21 — "tooooo large for desktop"). The avatar and
          // name were stretched over the full width for the same reason.
          //
          // Applied to the COMPACT render too, deliberately. The preview
          // frame is ~300px so this never binds there, and leaving it off
          // one of the two callers is precisely the divergence
          // previewLiveParity.test.js exists to stop.
          width: "100%",
          maxWidth: "30rem",
          marginLeft: "auto",
          marginRight: "auto",
        }}
      >
        {/* ── Profile ─────────────────────────────────────────── */}
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center" }}>
          {page?.profile_image ? (
            <img
              src={page.profile_image}
              alt={page?.display_name || "Profile"}
              loading="lazy"
              data-td-anim
              style={{ ...avatarCss(style), marginBottom: "0.875rem", ...anim(-2) }}
            />
          ) : (
            <div
              data-td-anim
              data-testid="profile-avatar-fallback"
              style={{
                ...avatarCss(style), marginBottom: "0.875rem", ...anim(-2),
                display: "flex", alignItems: "center", justifyContent: "center",
              }}
            >
              <User style={{ width: "45%", height: "45%", color: canvas.muted }} />
            </div>
          )}

          <h1 data-td-anim style={{ ...textCss(style.type.name, fonts), margin: 0, marginBottom: "0.375rem", lineHeight: 1.1, color: canvas.text, ...anim(-1.6) }}>
            {page?.display_name || "Your Name"}
          </h1>

          {page?.username && (
            <p data-td-anim style={{ ...textCss(style.type.handle, fonts), margin: 0, color: canvas.muted, ...anim(-1.3) }}>
              @{page.username}
            </p>
          )}

          {page?.category && (
            <p style={{ ...textCss(style.type.handle, fonts), margin: "0.25rem 0 0", color: canvas.muted, textTransform: "none" }}>
              {page.category}
            </p>
          )}

          {page?.bio && (
            <p data-td-anim style={{ ...textCss(style.type.bio, fonts), margin: "0.625rem 0 0", color: canvas.muted, lineHeight: 1.5, maxWidth: "17rem", ...anim(-1) }}>
              {page.bio}
            </p>
          )}

          {socials.length > 0 && (
            <div
              data-td-anim
              style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: px(style.social.gap), marginTop: "0.75rem", ...anim(-0.6) }}
            >
              {socials.map((key) => {
                const Icon = SOCIAL_ICONS[key];
                return (
                  <button
                    key={key}
                    type="button"
                    aria-label={key}
                    data-testid={`${isCompact ? "preview-" : ""}social-${key}`}
                    onClick={isInteractive ? () => onSocialClick(key, page.social_links[key]) : undefined}
                    style={socialCss(style)}
                  >
                    <Icon style={{ width: px(style.social.size), height: px(style.social.size) }} />
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* ── Tiles ───────────────────────────────────────────── */}
        {gridItems.length > 0 && (
          <div
            data-testid={`${isCompact ? "preview-" : ""}image-grid`}
            style={{ display: "grid", gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`, gap: px(spacing.tile) }}
          >
            {gridItems.map((item, i) => (
              <button
                key={item.id || i}
                type="button"
                data-td-anim
                data-testid={`${isCompact ? "preview-" : ""}grid-item-${i}`}
                onClick={isInteractive ? () => onGridClick(item) : undefined}
                style={{ position: "relative", padding: 0, border: "none", background: "none", cursor: isInteractive ? "pointer" : "default", ...anim(i) }}
              >
                {item.image_url ? (
                  <img
                    src={item.image_url}
                    alt={item.caption || ""}
                    loading="lazy"
                    style={tileCss(style)}
                    // A 404 or blocked image must not leave the browser's
                    // broken glyph and alt text sitting in the tile.
                    onError={(e) => {
                      e.currentTarget.style.visibility = "hidden";
                    }}
                  />
                ) : (
                  // No image yet. Keep the tile's own treatment — mat,
                  // border, radius, shadow — so the theme still reads.
                  <div
                    style={{
                      ...tileCss(style),
                      backgroundColor: canvas.dark
                        ? "rgba(255,255,255,0.07)"
                        : "rgba(0,0,0,0.07)",
                    }}
                    aria-hidden="true"
                  />
                )}
                {item.caption && (
                  <div style={captionCss(style)}>{item.caption}</div>
                )}
              </button>
            ))}
          </div>
        )}

        {/* ── Shop ──────────────────────────────────────────── */}
        {shopProducts.length > 0 && (
          <ShopGrid
            products={shopProducts}
            style={style}
            isCompact={isCompact}
            isInteractive={isInteractive}
            anim={anim}
            onProductClick={handlers.onProductClick || (() => {})}
          />
        )}

        {/* ── Links ───────────────────────────────────────────── */}
        {links.length > 0 && (
          <div style={{ display: "flex", flexDirection: "column", gap: px(spacing.gap) }}>
            {links.map((link, i) => (
              <button
                key={link.id || i}
                type="button"
                data-td-anim
                data-testid={`${isCompact ? "preview-" : ""}button-link-${i}`}
                onClick={isInteractive ? () => onButtonClick(link) : undefined}
                style={{ ...buttonCss(style), cursor: isInteractive ? "pointer" : "default", ...anim(i + gridItems.length) }}
              >
                {link.title || "Button link"}
              </button>
            ))}
          </div>
        )}

        {/* ── Instagram posts & reels ─────────────────────────── */}
        {embeds.length > 0 && (
          <EmbedGrid
            embeds={embeds}
            covers={embedCovers}
            style={style}
            columns={columns}
            spacing={spacing}
            canvas={canvas}
            isCompact={isCompact}
            isInteractive={isInteractive}
            anim={anim}
            // Continue the stagger past the tiles and links above rather
            // than restarting it, or the first embed animates in beside
            // the first grid tile instead of after the last link.
            animOffset={gridItems.length + links.length}
            onEmbedClick={handlers.onEmbedClick || (() => {})}
          />
        )}

        {/* ── Email capture ───────────────────────────────────── */}
        {emailCfg.enabled && (
          <div
            data-testid={isCompact ? "preview-email-capture" : "email-capture-block"}
            style={{
              padding: "1rem",
              borderRadius: radiusCss(style.tile.radius),
              border: borderCss(style.tile.border),
              boxShadow: shadowCss(style.tile.shadow),
              textAlign: "center",
            }}
          >
            <h3 style={{ ...textCss(style.type.handle, fonts), color: canvas.text, margin: 0, marginBottom: "0.25rem", textTransform: "none" }}>
              {emailCfg.heading || "Stay in the loop"}
            </h3>
            <p style={{ ...textCss(style.type.bio, fonts), color: canvas.muted, margin: "0 0 0.75rem" }}>
              {emailCfg.subtext || "Get updates straight to your inbox"}
            </p>
            {isInteractive && leadState?.status === "success" ? (
              <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "0.5rem", color: canvas.text }}>
                <CheckCircle style={{ width: "1.25rem", height: "1.25rem" }} />
                <span>{emailCfg.success_message || "You're in!"}</span>
              </div>
            ) : (
              <div style={{ display: "flex", gap: "0.5rem" }}>
                <input
                  type="email"
                  value={leadState?.email || ""}
                  onChange={isInteractive ? (e) => leadState?.onChange(e.target.value) : undefined}
                  readOnly={!isInteractive}
                  placeholder="your@email.com"
                  data-testid="email-capture-input"
                  aria-label="Email address"
                  style={{
                    flex: 1, minWidth: 0, padding: "0.625rem 0.75rem",
                    borderRadius: radiusCss(style.button.radius),
                    border: borderCss(style.tile.border),
                    background: canvas.dark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)",
                    color: canvas.text, outline: "none",
                    ...textCss(style.type.bio, fonts),
                  }}
                />
                <button
                  type="button"
                  onClick={isInteractive ? () => leadState?.onSubmit() : undefined}
                  data-testid="email-capture-submit"
                  style={{ ...buttonCss(style), width: "auto", paddingLeft: "1rem", paddingRight: "1rem" }}
                >
                  {emailCfg.cta_text || "Subscribe"}
                </button>
              </div>
            )}
            {leadState?.error && (
              <p style={{ ...textCss(style.type.bio, fonts), color: canvas.muted, marginTop: "0.5rem" }}>{leadState.error}</p>
            )}
          </div>
        )}

        {/* ── Footer ──────────────────────────────────────────── */}
        {wantChrome && (
          <div style={{ marginTop: "auto", textAlign: "center", paddingTop: "0.5rem" }}>
            <a
              href="https://tadlo.ai"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="tadlo-footer-link"
              style={{
                ...textCss({ size: style.footer.size, weight: 500, spacing: style.footer.spacing, upper: style.footer.upper, font: "ui" }, fonts),
                color: canvas.muted,
                opacity: style.footer.opacity,
                textDecoration: "none",
              }}
            >
              Made with Tadlo
            </a>
          </div>
        )}
      </div>
    </div>
  );

  if (!scaled) return inner;
  return (
    <div style={{ overflow: "hidden" }} data-testid="bio-scaled-wrapper">
      {inner}
    </div>
  );
}

// `platformFromUrl` and `ExternalLink` remain imported for link metadata used
// by callers; referenced here so lint sees them as intentional.
export const __linkHelpers = { platformFromUrl, ExternalLink };
